import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { Container } from 'src/components/Container';
import { Icon } from 'src/components/Icon';
import { Meta } from 'src/components/Meta';
import { SectionHeader } from 'src/components/SectionHeader';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import { useHQPermissionCheck } from 'src/shared/HQ/useHQPermissionCheck';
import toast from 'src/utils/toast';
import * as Yup from 'yup';

import { useFranchiseFeePage } from './useFranchiseFeePage';

const createValidationSchema = (minStartDate?: string) =>
  Yup.object({
    name: Yup.string().required('Name is required'),
    feeType: Yup.string().required('Fee type is required'),
    percentageAmount: Yup.number().when('feeType', {
      is: 'percentage',
      then: (schema) =>
        schema
          .required('Percentage amount is required')
          .min(1, 'Percentage amount must be greater than 0'),
      otherwise: (schema) => schema.nullable(),
    }),
    amount: Yup.number().when('feeType', {
      is: 'flat',
      then: (schema) =>
        schema
          .required('Amount is required')
          .min(0.01, 'Amount must be greater than 0'),
      otherwise: (schema) => schema.nullable(),
    }),
    startDate: Yup.date()
      .min(
        minStartDate
          ? new Date(minStartDate)
          : new Date(new Date().setHours(0, 0, 0, 0)),
        minStartDate
          ? 'Start date cannot be earlier than the original start date'
          : 'Start date cannot be in the past'
      ),
    endDate: Yup.date()
      .min(Yup.ref('startDate'), 'End date must be after start date'),
  });

export default function FranchiseFeeFormPage() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'franchiseFees.form',
  });
  const { session } = useSession();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { canEditHQFranchiseFees } = useHQPermissionCheck();
  const { franchiseFee, createFranchiseFee, updateFranchiseFee } =
    useFranchiseFeePage(id);

  // Redirect if user doesn't have edit permission
  if (!canEditHQFranchiseFees()) {
    navigate('/franchise-fees/fees');
    return null;
  }

  const initialValues = {
    name: franchiseFee?.name || '',
    feeType: franchiseFee?.feeType || 'flat',
    percentageAmount: franchiseFee?.percentageAmount || null,
    amount: franchiseFee?.amount || null,
    startDate: franchiseFee?.startDate || '',
    endDate: franchiseFee?.endDate || '',
  };

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      if (isEdit && id) {
        await updateFranchiseFee.mutateAsync({ id, ...values });
        toast.success('Franchise fee updated successfully');
      } else {
        await createFranchiseFee.mutateAsync(values);
        toast.success('Franchise fee created successfully');
      }
      navigate('/franchise-fees/fees');
    } catch (error) {
      const action = isEdit ? 'update' : 'create';
      toast.error(`Failed to ${action} franchise fee`);
    }
  };

  const feeTypeOptions = [
    { value: 'flat', label: 'Flat' },
    { value: 'percentage', label: 'Percentage' },
  ];

  const pageTitle = isEdit ? t('editTitle') : t('newTitle');
  const submitButtonText = isEdit ? t('updateFee') : t('createFee');
  const isSubmitting = isEdit
    ? updateFranchiseFee.isPending
    : createFranchiseFee.isPending;

  // Show loading state for edit page while fetching data
  if (isEdit && !franchiseFee) {
    return (
      <>
        <Meta title={pageTitle} brand={session?.brand} />
        <AppFrameConfig
          id="franchise-fee-form"
          containerMaxWidth={'lg'}
          title={pageTitle}
        />
        <Container>
          <Card sx={{ p: 4 }}>
            <div>Loading...</div>
          </Card>
        </Container>
      </>
    );
  }

  return (
    <>
      <Meta title={pageTitle} brand={session?.brand} />
      <AppFrameConfig
        id="franchise-fee-form"
        containerMaxWidth={'lg'}
        title={pageTitle}
        actions={
          <Button
            variant="outlined"
            startIcon={
              <Icon icon={icon({ name: 'arrow-left', style: 'regular' })} />
            }
            onClick={() => navigate('/franchise-fees/fees')}
          >
            {t('backToFees')}
          </Button>
        }
      />

      <Container>
        <Card>
          <SectionHeader
            title={t('franchiseFeesSettingsTitle')}
            bottomDivider
          />
          <Box sx={{ p: 4 }}>
            <Formik
              initialValues={initialValues}
              validationSchema={createValidationSchema(
                isEdit ? franchiseFee?.startDate : undefined
              )}
              onSubmit={handleSubmit}
              enableReinitialize
              context={{ isEdit }}
            >
              {({
                isSubmitting: formikIsSubmitting,
                isValid,
                values,
                setFieldValue,
                errors,
              }) => (
                <Form>
                  <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <FormTextField
                      name="name"
                      label={t('name')}
                      fullWidth
                      required
                    />

                    <FormTextField
                      name="feeType"
                      label={t('feeType')}
                      options={feeTypeOptions}
                      fullWidth
                      onChange={(event) => {
                        const newValue = event?.target.value;
                        if (newValue === 'percentage') {
                          setFieldValue('amount', null);
                        } else {
                          setFieldValue('percentageAmount', null);
                        }
                      }}
                      required
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    {values.feeType === 'percentage' && (
                      <FormTextField
                        name="percentageAmount"
                        label={t('percentageAmount')}
                        type="number"
                        fullWidth
                        required
                      />
                    )}
                    {values.feeType === 'flat' && (
                      <FormTextField
                        name="amount"
                        label={t('amount')}
                        type="number"
                        fullWidth
                        required
                      />
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <FormTextField
                      name="startDate"
                      label={t('startDate')}
                      type="date"
                      fullWidth
                      helperText={errors.startDate || t('startDateHelperText')}
                    />

                    <FormTextField
                      name="endDate"
                      label={t('endDate')}
                      type="date"
                      fullWidth
                    />
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('AllSites')}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      justifyContent: 'flex-end',
                      mt: 4,
                    }}
                  >
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={() => navigate('/franchise-fees/fees')}
                    >
                      {t('cancel')}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={formikIsSubmitting || !isValid || isSubmitting}
                      loading={isSubmitting}
                    >
                      {submitButtonText}
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          </Box>
        </Card>
      </Container>
    </>
  );
}
